import { Button, Col, Collapse, Row, Space, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  SaveOutlined,
  UndoOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  readAction,
  updateAction,
  clearOperationResultAction,
} from "../../store/company/companyActions";
import CompanyCommercialPanel from "./panels/CompanyCommercialPanel";
import CompanyContractPanel from "./panels/CompanyContractPanel";
import CompanyGeneralPanel from "./panels/CompanyGeneralPanel";
import CompanyServicesPanel from "./panels/CompanyServicesPanel";
import companySchema from "./companySchema";
import openNotificationOperationResult from "../shared/openNotificationOperationResult";

const allPanelKeys = ["1", "2", "3", "4"];

const defaultValues = {
  // General
  CompanyName: "",
  Address1: "",
  Address2: "",
  City: "",
  StateProvinceCode: "",
  ZipPostalCode: "",

  // Contract
  ContractSignedDate: null,
  ContractDocuSignLink: "",
  ContractDocuSignEnvelopeId: "",

  // Signatory
  SignatoryName: "",
  SignatoryTitle: "",
  SignatoryEmail: "",
  SignatoryPhone: "",

  // MK154A
  MK154AIsSubmitted: false,
  MK154AConfirmedDate: null,
  MK154ANotes: "",
};

const CompanyForm = () => {
  const dispatch = useDispatch();
  const [activeKeys, setActiveKeys] = useState(allPanelKeys);

  const companyKey = useSelector(
    (state) => state.actor?.details?.data?.BrokerId,
  );
  const { data, loading, operationResult } = useSelector(
    (state) => state.company,
  );

  const {
    handleSubmit,
    reset,
    control,
    trigger,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(companySchema),
  });

  useEffect(() => {
    if (companyKey) {
      dispatch(readAction(companyKey));
    }
  }, [companyKey, dispatch]);

  useEffect(() => {
    if (data) {
      reset({
        // General
        CompanyName: data.CompanyName ?? "",
        Address1: data.Address1 ?? "",
        Address2: data.Address2 ?? "",
        City: data.City ?? "",
        StateProvinceCode: data.StateProvinceCode ?? "",
        ZipPostalCode: data.ZipPostalCode ?? "",

        // Contract
        ContractSignedDate: data.ContractSignedDate ?? null,
        ContractDocuSignLink: data.ContractDocuSignLink ?? "",
        ContractDocuSignEnvelopeId: data.ContractDocuSignEnvelopeId ?? "",

        // Signatory
        SignatoryName: data.SignatoryName ?? "",
        SignatoryTitle: data.SignatoryTitle ?? "",
        SignatoryEmail: data.SignatoryEmail ?? "",
        SignatoryPhone: data.SignatoryPhone ?? "",

        // MK154A
        MK154AIsSubmitted: data.MK154AIsSubmitted ?? false,
        MK154AConfirmedDate: data.MK154AConfirmedDate ?? null,
        MK154ANotes: data.MK154ANotes ?? "",
      });
    }
  }, [data, reset]);

  useEffect(() => {
    openNotificationOperationResult(operationResult, () =>
      dispatch(clearOperationResultAction()),
    );
  }, [operationResult]);

  const onActiveKeyChangeHandler = (keys) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const handleSave = (values) => {
    dispatch(
      updateAction({
        ...values,
        CompanyKey: companyKey,
      }),
    );
  };

  const handleResetValues = () => {
    reset();
    trigger();
  };

  const handleExpandAll = () => {
    setActiveKeys(allPanelKeys);
  };

  const handleCollapseAll = () => {
    setActiveKeys([]);
  };

  const items = useMemo(
    () => [
      {
        key: "1",
        label: "General",
        children: <CompanyGeneralPanel control={control} />,
      },
      {
        key: "2",
        label: "Contract & Authorization",
        children: <CompanyContractPanel control={control} />,
      },
      {
        key: "3",
        label: "Services",
        children: <CompanyServicesPanel />,
      },
      // {
      //   key: "4",
      //   label: "Commercial / Pricing Schedules",
      //   children: <CompanyCommercialPanel />,
      // },
    ],
    [control],
  );

  return (
    <Spin spinning={loading}>
      <Row style={{ paddingBottom: 4 }}>
        <Col span={24}>
          <Space>
            <Button
              size="small"
              disabled={!isDirty || !isValid}
              loading={loading}
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit(handleSave)}
            >
              Save
            </Button>
            <Button
              size="small"
              type="dashed"
              disabled={!isDirty || loading}
              icon={<UndoOutlined />}
              onClick={handleResetValues}
            >
              Reset Values
            </Button>
            <Button
              size="small"
              icon={<DownOutlined />}
              onClick={handleExpandAll}
            >
              Expand All
            </Button>
            <Button
              size="small"
              icon={<UpOutlined />}
              onClick={handleCollapseAll}
            >
              Collapse All
            </Button>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Collapse
            items={items}
            activeKey={activeKeys}
            onChange={onActiveKeyChangeHandler}
            size="small"
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default CompanyForm;
